import React, {
  useCallback,
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  forwardRef,
  ReactNode,
  Children,
  isValidElement,
  RefObject,
  CSSProperties,
} from "react";
import { CardProps, CardWithStatics } from "./types";
import cx from "components/src/utils/cx";
import { roundedClasses } from "shared/src/styles/roundedStyles";
import {
  tailwindSemanticBgColors,
  tailwindSemanticBorderColors,
} from "shared/src/styles/colorStyles";
import { cssColor } from "shared/src/utils/cssUtils";

import get from "lodash-es/get";
import { CardHeader } from "./components/CardHeader";
import { CardPreFooter } from "./components/CardPreFooter";
import { CardFooter } from "./components/CardFooter";
import { CardSubHeader } from "./components/CardSubHeader";
import { addProps } from "shared/src/utils/addProps";
import { gapClasses } from "../Grid/js/gridClasses";
import useForwardRef from "shared/src/hooks/useForwardRef";
import { Header } from "./components/Header";
import { randomIdString } from "shared/src/utils/randomIdString";
// import { isiOS, isSafari } from "shared/src/utils/supports";
import { useSize } from "shared/src/hooks/useSize";

const isValidCardState = ["open", "expanded", "closed"];

const _Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      id = "",
      rounded = "sm",
      bg = "bg-default",
      noPad,
      showBorder = true,
      shadow,
      className = "",
      cardBodyClasses = "",
      cardBodyStyles,
      title,
      description,
      actionText,
      children,
      collapsible,
      expandable,
      restrictTo,
      gap = "none",
      flow = "column",
      initiallyOpen = true,
      defaultHeader,
      headerBg,
      headerDetails = [],
      animation = true,
      animationSpeed = 200,
      maxHeight,
      minHeight,
      borderColor = "fg-subtle",
      mountHiddenContent = true,
      setCardState,
      preventHeaderClickPropagation = false,
      headerConfig,
      onToggle,
      onCardClick,
      onExpand,
      ...rest
    }: CardProps,
    ref
  ) => {
    const [_styles, setStyles] = useState({});
    const [_bodyStyles, setBodyStyles] = useState({});

    const canExpandHistory = useRef<boolean | null>(null);
    const prevMaxHeight = useRef<number | null>(null);
    const defaultIdStr = useRef(randomIdString());
    const itemRef = useForwardRef<HTMLDivElement | null>(
      ref as RefObject<HTMLDivElement>
    );
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const cardContentsRef = useRef<{ body: HTMLDivElement | null, footer: HTMLDivElement | null, header: HTMLDivElement | null }>({ body: null, footer: null, header: null});

    const _id = id ? id : `cui__card-${defaultIdStr.current}`;
    const _bodyId = `cui__card-body-${id ? id : defaultIdStr.current}`;

    const [isOpen, setOpen] = useState(initiallyOpen);
    const [canExpand, setCanExpand] = useState(false);

    const isExpandable = initiallyOpen && expandable && !!maxHeight;
    
    const cardState = useMemo(() => {
      if (setCardState && isValidCardState.includes(setCardState))
        return setCardState;
      return isOpen && isExpandable
        ? "expanded"
        : !isExpandable && isOpen
        ? "open"
        : "closed";
    }, [isOpen, isExpandable, setCardState]);

    const usesDefaultHeader =
      !!title || !!description || !!actionText || !!headerDetails.length;

    let footer: ReactNode,
      header: ReactNode,
      preFooter: ReactNode,
      subHeader: ReactNode;
    let body: ReactNode[] = [];

    const setOpenState = (v: any) => {
      onToggle && onToggle({ isOpen: !v, cardState: cardState, id });
      return !v;
    };

    const handleKeyDown = useCallback(
      (e) => {
        const { key, code, keyCode, target, currentTarget } = e;
        const wasSpaceKey = key === " " || code === "Space" || keyCode === 32;
        if (target === currentTarget && (key === "Enter" || wasSpaceKey)) {
          e.preventDefault();
          collapsible && setOpen(setOpenState(isOpen));
        }
      },
      [isOpen]
    );

    const onClick = useCallback((e) => {
      preventHeaderClickPropagation && e.stopPropagation();
      collapsible && setOpen(setOpenState(isOpen));
    }, [collapsible, isOpen]);

    Children.forEach(children, (child) => {
      if (!child || (!isValidElement(child) && typeof child !== "string"))
        return;
      let _name: string | null;
      if (typeof child === "string") {
        body.push(child);
      } else if (typeof child === "object") {
        // @ts-ignore
        _name =
          get(child.props, "mdxType", null) ||
          get(child.type, "displayName", null)?.replace(".", "") ||
          get(child.type, "name", null);
        // @ts-ignore
        switch (_name) {
          case "CardFooter":
            footer = { ...child };
            footer = addProps(footer, { noPad, isOpen, className: `cui__card-footer ${footer.props.className}` });
            break;
          case "CardPreFooter":
            preFooter = { ...child };
            preFooter = addProps(preFooter, { noPad });
            break;
          case "CardHeader":
            header = { ...child };
            const _props: any = { onClick, isOpen };
            if (collapsible !== undefined) _props.collapsible = collapsible;
            if (headerDetails.length) _props.details = headerDetails;
            if (headerBg) _props.bg = headerBg;
            header = addProps(header, _props);
            break;
          case "CardSubHeader":
            subHeader = { ...child };
            subHeader = addProps(subHeader, { noPad });
            break;
          default:
            // @ts-ignore
            if (
              _name &&
              restrictTo &&
              Array.isArray(restrictTo) &&
              restrictTo.length &&
              restrictTo.includes(_name)
            ) {
              body.push(child);
            } else if (!restrictTo) {
              body.push(child);
            }
        }
      }
    });

    const headProps = useMemo(
      () => ({
        title,
        description,
        actionText,
        isOpen,
        onClick,
        collapsible,
        details: headerDetails,
        bg: headerBg,
        ...headerConfig
      }),
      [
        title,
        description,
        actionText,
        isOpen,
        onClick,
        collapsible,
        headerDetails,
        headerBg,
        headerConfig
      ]
    );

    const _borderColor =
      borderColor in tailwindSemanticBorderColors
        ? tailwindSemanticBorderColors[borderColor]
        : cssColor(borderColor);
    const _borderStyles = _borderColor.includes("cui-border")
      ? {}
      : { borderColor: _borderColor };

    useEffect(()=>{
      if (itemRef.current !== null && expandable) {
        if (!cardContentsRef.current.body && !cardContentsRef.current.footer && !cardContentsRef.current.header) {
          cardContentsRef.current.body = itemRef.current.querySelector(".cui__card-body");
          cardContentsRef.current.footer = itemRef.current.querySelector(".cui__card-footer");
          cardContentsRef.current.header = itemRef.current.querySelector(".cui__card-header");
        }
        const _cardBodyHeight = cardContentsRef.current?.body?.clientHeight ?? 0;
        const _cardFooterHeight = cardContentsRef.current?.footer?.clientHeight ?? 0;
        const _cardHeaderHeight = cardContentsRef.current?.header?.clientHeight ?? 0;

        const cardContents = _cardBodyHeight + _cardFooterHeight + _cardHeaderHeight;

        if (cardState === "expanded") {
          canExpandHistory.current = cardContents > Math.max(itemRef.current.clientHeight, Number(maxHeight));
        } else if (cardState === "open") {
          canExpandHistory.current = itemRef.current.clientHeight > Number(maxHeight);
        } else {
          canExpandHistory.current = false;
        }
        setCanExpand(!!canExpandHistory.current);
        onExpand && onExpand({ canExpand: !!canExpandHistory.current, isExpandable: Boolean(canExpandHistory.current && cardState === "expanded") });
      }
    }, [cardState, isExpandable, maxHeight, minHeight, expandable]);
      

    const _maxHeight = typeof maxHeight !== "undefined" &&  expandable && cardState === "expanded" ? maxHeight : "";
    const _minHeight = typeof minHeight !== "undefined" && typeof minHeight === "number" ? minHeight - 1 : "";

    useLayoutEffect(() => {
      if (itemRef.current !== null && bodyRef.current !== null && (cardContentsRef.current.header || !isExpandable)) {
        /**
         * Card Height
         */
        
        const _cardHeaderHeight = cardContentsRef.current?.header?.clientHeight ?? 0;
        
        const _maxHeight = typeof maxHeight !== "undefined" &&  expandable && cardState === "expanded" ? maxHeight - _cardHeaderHeight : "";
        const _minHeight = typeof minHeight !== "undefined" && typeof minHeight === "number" ? minHeight - 1 - _cardHeaderHeight : "";

        let cardHeight: Record<PropertyKey, CSSProperties["height"]> = {
          height: 0,
        };
        if (cardState === "expanded")
          cardHeight = {
            minHeight: _minHeight,
            maxHeight: _maxHeight,
            height: "auto", //isiOS || isSafari ? "auto" : "calc-size(auto,size)" - FIX: Look into this, this is animation but it breaks on Firefox and other things.
        };

        if (cardState === "open")
          cardHeight = {
            minHeight: _minHeight,
            // maxHeight: _maxHeight,
            height: "auto", //isiOS || isSafari ? "auto" : "calc-size(auto,size)" - FIX: Look into this, this is animation but it breaks on Firefox and other things.
        };

        setStyles({
          ...cardHeight,
          transitionDuration: `${animationSpeed}`,
        });
      }
    }, [
      maxHeight,
      isOpen,
      isExpandable,
      cardState,
      mountHiddenContent,
      animationSpeed
    ]);  

    const shouldRenderContent = mountHiddenContent || isOpen;
  
    return (
      <div
        className={cx(
          {
            [roundedClasses[rounded]]: true,
            [tailwindSemanticBgColors[bg]]: true,
            "cui-py-sm": !noPad && !footer && !header && !usesDefaultHeader,
            "cui-pt-sm": !noPad && !header && !usesDefaultHeader,
            "cui-pb-sm": !noPad && !footer && isOpen,
            [`cui-border-1 cui-border-solid ${_borderColor}`]: showBorder,
            [gapClasses[gap]]: isOpen,
            [`${className || ""}`]: true,
          },
          `cui__card-wrapper cui-grid cui-relative cui-overflow-hidden cui-box-border focus-visible:cui-ring-2 focus-visible:cui-ring-fg-focus focus-visible:cui-ring-offset-2 focus-visible:cui-outline-none`
        )}
        tabIndex={0}
        data-cy="cui__card"
        ref={itemRef}
        id={_id}
        onClick={onCardClick}
        onKeyDown={handleKeyDown}
        aria-controls={_bodyId}
        aria-expanded={isOpen ? "true" : "false"}
        role="button"
        style={{
          ..._borderStyles, 
          minHeight: _minHeight,
          maxHeight: _maxHeight,
          boxShadow: shadow ? `var(--box-shadow-${shadow})` : undefined,
        }}
        {...rest}
      >
        <Header
          usesDefaultHeader={usesDefaultHeader}
          header={header}
          defaultHeader={defaultHeader}
          headProps={headProps}
        />
        {shouldRenderContent && (
          <>
            <div
              className={cx(
                {
                  "cui-transition-opacity cui-transition-transform cui-transition-colors": animation,
                },
                "cui__card-contents cui-w-auto cui-overflow-hidden cui-flex cui-flex-col cui-justify-between"
              )}
              id={_bodyId}
              aria-labelledby={_id}
              style={_styles}
              role="region"
            >
              {subHeader ? subHeader : null}
              {body ? (
                <div
                  className={cx(
                    {
                      "cui-px-md": !noPad,
                      "cui-flex cui-flex-col": flow === "column",
                      "cui-flex cui-flex-wrap": flow === "row",
                      [gapClasses[gap]]: true,
                      [cardBodyClasses]: true
                    },
                    "cui__card-body"
                  )}
                  ref={bodyRef}
                >
                  {body}
                </div>
              ) : null}
              {preFooter ? preFooter : null}
              {footer ? footer : null}
            </div>
          </>
        )}
      </div>
    );
  }
) as CardWithStatics;

export const Card = Object.assign({}, _Card, {
  Header: CardHeader,
  SubHeader: CardSubHeader,
  PreFooter: CardPreFooter,
  Footer: CardFooter,
  displayName: "Card",
});
